import { useRef, useState } from "react"
import { ArrowRight, Check, Copyright, FileCheck, FileText, Feather, Layers, LayoutGrid, Quote, ShieldCheck, SquarePen, Star } from "lucide-react"

import writer1 from "@/images/openHouse/writer-1.jpg"
import writer2 from "@/images/openHouse/writer-2.jpg"
import writer3 from "@/images/openHouse/writer-3.jpg"
import writer4 from "@/images/openHouse/writer-4.jpg"
import { money } from "@/lib/openHouse/bookWritingSteps"
import { ARRIVAL, GUEST } from "@/lib/openHouse/wording"
import { writerRateFor } from "@/lib/post/services/bookWriting"
import { cn } from "@/lib/utils"

// The arrival page (new-flow-wording §34): where every guest lands, doing a
// landing page's job around the start choices, so ads and the site link here
// directly. Writing holds book writing, the paid sample and bundles; editing and
// every other service open today's form (spec A1–A2), through sign up for a guest. `openWriting` arrives
// with book writing already open (?start=writing, the ghostwriting page's links).
const WRITING_ICONS = { book_writing: Feather, sample: FileText, bundle: Layers }
const CHECK_ICONS = [ShieldCheck, FileCheck, Copyright]
// Specialists from the Authority Book Program page; decorative, so no alt text.
const WRITERS = [writer1, writer2, writer3, writer4]

function IconTile({ icon: Glyph, on }) {
  return (
    <span
      className={cn(
        "grid size-12 shrink-0 place-items-center rounded-[14px] border transition-colors",
        on ? "border-primary bg-primary text-primary-foreground" : "border-border bg-[var(--red-soft)] text-primary dark:border-line-2 dark:text-[var(--red-dark)]"
      )}
    >
      <Glyph className="size-[22px]" strokeWidth={1.8} />
    </span>
  )
}

// The round arrow every clickable choice carries; it fills red and nudges
// right when its `group` is hovered or focused.
function Go({ small }) {
  return (
    <span
      className={cn(
        "grid shrink-0 place-items-center rounded-full bg-foreground text-background transition-[background-color,transform] duration-150 group-hover:translate-x-0.5 group-hover:bg-primary group-hover:text-primary-foreground group-focus-visible:bg-primary group-focus-visible:text-primary-foreground",
        small ? "size-[26px]" : "size-[34px]"
      )}
    >
      <ArrowRight className={small ? "size-[13px]" : "size-4"} strokeWidth={2.4} />
    </span>
  )
}

function ChoiceBody({ icon, on, label, description, badge }) {
  return (
    <>
      <IconTile icon={icon} on={on} />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="font-display text-[16px] font-semibold tracking-tight text-foreground">{label}</span>
        <span className="text-[13.5px] leading-relaxed text-muted-foreground">{description}</span>
        {badge && (
          <span className="mt-1.5 self-start rounded-full bg-[var(--red-soft)] px-2.5 py-0.5 text-[12.5px] font-bold text-primary dark:text-[var(--red-dark)]">{badge}</span>
        )}
      </span>
      <Go />
    </>
  )
}

const ROW = "group flex w-full cursor-pointer items-center gap-4 p-4 text-left outline-none"
const LIFT =
  "transition-[border-color,background-color,box-shadow,transform] duration-150 hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-primary/40"
// Every choice is a raised button: a visible edge and a small shadow at rest,
// a red edge and a deeper shadow on hover. Dark mode keeps it a step below the card.
const CHOICE = cn(
  LIFT,
  "border-[1.5px] border-line-2 bg-card shadow-[0_1px_0_rgba(26,26,23,0.06),0_3px_8px_rgba(26,26,23,0.06)] hover:border-primary hover:shadow-[0_10px_24px_rgba(26,26,23,0.10)]",
  "dark:bg-[var(--surface3)] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_2px_0_rgba(0,0,0,0.35),0_6px_14px_rgba(0,0,0,0.30)] dark:hover:border-primary dark:hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_14px_30px_rgba(0,0,0,0.45)]"
)

function StartCard({ catalog, links, onStart, openWriting, cardRef }) {
  const [writing, setWriting] = useState(openWriting)
  const name = (key) => (catalog.services || []).find((s) => s.key === key)?.name
  const writingServices = [["book_writing", name("book_writing")], ["sample", name("sample")], ["bundle", "Pre-Built Bundle"]]
  const rate = writerRateFor("Rising", false, { rates: catalog.rates })

  return (
    <div
      ref={cardRef}
      className="relative flex w-full min-w-0 flex-col gap-3 rounded-[22px] border border-border bg-card p-6 shadow-[0_30px_70px_rgba(26,26,23,0.16),0_0_0_1px_rgba(26,26,23,0.04)] dark:border-line-2 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_40px_90px_rgba(0,0,0,0.6)] max-[561px]:p-4"
    >
      <h2 className="mx-0.5 mb-1.5 font-display text-[22px] font-semibold tracking-tight text-foreground">{GUEST.startH}</h2>

      <div className={cn("rounded-2xl transition-colors", writing ? "border-[1.5px] border-primary bg-[var(--red-soft)]/40 dark:border-primary/75 dark:bg-primary/[0.07]" : "")}>
        <button type="button" aria-expanded={writing} onClick={() => setWriting(!writing)} className={cn(ROW, "rounded-2xl", writing ? "" : CHOICE)}>
          <ChoiceBody
            icon={Feather}
            on={writing}
            label={GUEST.writeBook}
            description={GUEST.writeBookDesc}
            badge={rate != null ? ARRIVAL.writingFrom(money(rate)) : undefined}
          />
        </button>
        {writing && (
          <div className="grid gap-2.5 px-4 pb-4 min-[561px]:grid-cols-3">
            {writingServices.map(([key, label]) => {
              const Glyph = WRITING_ICONS[key]
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => onStart(key)}
                  className={cn(
                    CHOICE,
                    "group flex cursor-pointer flex-col items-start gap-2.5 rounded-xl px-3 pt-3.5 pb-3 text-left text-[14px] font-bold text-foreground outline-none hover:bg-[var(--red-soft)]"
                  )}
                >
                  <span className="flex w-full items-center justify-between gap-2">
                    <span className="grid size-9 place-items-center rounded-[10px] bg-[var(--red-soft)] text-primary dark:text-[var(--red-dark)]">
                      <Glyph className="size-5" strokeWidth={1.8} />
                    </span>
                    <Go small />
                  </span>
                  {label}
                </button>
              )
            })}
          </div>
        )}
      </div>

      <a href={links.editing} className={cn(ROW, "rounded-2xl no-underline", CHOICE)}>
        <ChoiceBody icon={SquarePen} label={GUEST.editBook} description={GUEST.editBookDesc} />
      </a>
      <a href={links.other} className={cn(ROW, "rounded-2xl no-underline", CHOICE)}>
        <ChoiceBody icon={LayoutGrid} label={GUEST.somethingElse} description={GUEST.somethingElseDesc} />
      </a>
      <p className="mx-0.5 mt-1.5 text-[13px] leading-relaxed text-muted-foreground">{GUEST.paymentTiming}</p>
    </div>
  )
}

function Faces({ size = "size-[46px]", ring = "border-card" }) {
  return (
    <span className="flex">
      {WRITERS.map((src, i) => (
        <img key={src} src={src} alt="" className={cn(size, ring, "rounded-full border-[3px] object-cover", i > 0 && "-ml-3")} />
      ))}
    </span>
  )
}

function Stars({ className }) {
  return (
    <span aria-hidden="true" className={cn("flex text-[#F59E0B] dark:text-[#FFB547]", className)}>
      {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="size-[15px]" fill="currentColor" strokeWidth={0} />)}
    </span>
  )
}

const H2 = "font-display text-[34px] font-bold tracking-[-0.8px] text-foreground max-[561px]:text-[26px]"
const WRAP = "mx-auto w-full max-w-[1180px] px-8 max-[561px]:px-4"
const CARD =
  "rounded-[18px] border border-border bg-card shadow-[0_1px_2px_rgba(26,26,23,0.04),0_14px_34px_rgba(26,26,23,0.07)] dark:border-line-2 dark:bg-[var(--surface3)] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_18px_44px_rgba(0,0,0,0.4)]"
// The hero glows: red light behind the start card, warm light bottom-left and a
// faint grid, so the page reads as layers rather than one flat surface.
const HERO_BG =
  "[background:radial-gradient(900px_560px_at_82%_18%,rgba(255,31,74,0.16),transparent_62%),radial-gradient(760px_520px_at_4%_100%,rgba(255,166,77,0.16),transparent_60%),linear-gradient(180deg,var(--card)_0%,var(--background)_100%)] dark:[background:radial-gradient(900px_560px_at_82%_18%,rgba(255,61,99,0.30),transparent_62%),radial-gradient(760px_520px_at_4%_100%,rgba(255,140,90,0.13),transparent_60%),linear-gradient(180deg,#202020_0%,var(--background)_100%)]"
const HERO_GRID =
  "pointer-events-none absolute inset-0 [background-image:linear-gradient(rgba(26,26,23,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(26,26,23,0.05)_1px,transparent_1px)] [background-size:56px_56px] [mask-image:radial-gradient(ellipse_70%_70%_at_70%_30%,#000_20%,transparent_75%)] dark:[background-image:linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)]"
const FEATURED =
  "border-primary/25 [background:linear-gradient(160deg,#FFF0F3_0%,var(--card)_70%)] shadow-[0_24px_60px_rgba(255,31,74,0.12)] dark:border-primary/35 dark:[background:linear-gradient(160deg,#3A1F26_0%,var(--surface3)_70%)] dark:shadow-[0_24px_60px_rgba(255,61,99,0.14),0_18px_44px_rgba(0,0,0,0.4)]"

function Review({ text, featured }) {
  return (
    <figure className={cn(CARD, "relative flex flex-col gap-5 overflow-hidden p-7", featured ? cn(FEATURED, "min-w-0 flex-[1_1_520px] justify-between p-10 max-[561px]:p-7") : "flex-auto")}>
      <Quote aria-hidden="true" className={cn("text-primary", featured ? "size-10" : "size-[24px]")} fill="currentColor" strokeWidth={1} />
      <blockquote className={cn(featured ? "font-display text-[23px] font-medium leading-[1.45] tracking-tight max-[561px]:text-[19px]" : "text-[16px] leading-relaxed", "text-foreground")}>{text}</blockquote>
      <figcaption className="mt-auto flex items-center gap-2.5 text-[13px] font-semibold text-muted-foreground">
        <Stars />
        {ARRIVAL.reviewer}
      </figcaption>
    </figure>
  )
}

// The start page (/post-project/start): the start card alone, for visitors who
// already chose to post from the site's own Post a Project buttons.
export function StartPage({ catalog, links, onStart, openWriting = false }) {
  return (
    <div className={cn(WRAP, "flex justify-center pt-12 pb-16 max-[561px]:pt-6")}>
      <div className="w-full max-w-[520px]">
        <StartCard catalog={catalog} links={links} onStart={onStart} openWriting={openWriting} />
      </div>
    </div>
  )
}

export function Arrival({ catalog, links, onStart, openWriting = false }) {
  const cardRef = useRef(null)
  const toStart = () => {
    cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
    cardRef.current?.querySelector("button")?.focus({ preventScroll: true })
  }
  const [featured, ...others] = ARRIVAL.reviews
  const [books, booksLabel] = ARRIVAL.stats[0]

  return (
    <div>
      <section className={cn("relative overflow-x-clip border-b border-border dark:border-line-2", HERO_BG)}>
        <div aria-hidden="true" className={HERO_GRID} />
        <div className={cn(WRAP, "relative flex flex-col gap-12 pt-14 pb-14 min-[900px]:flex-row min-[900px]:items-center min-[900px]:pb-[120px]")}>
          <div className="flex min-w-0 flex-1 flex-col gap-5">
            <h1 className="font-display text-[52px] font-bold leading-[1.08] tracking-[-1.4px] text-foreground max-[561px]:text-[34px]">{ARRIVAL.headline}</h1>
            <p className="max-w-[540px] text-[19px] leading-normal text-muted-foreground max-[561px]:text-[17px]">{ARRIVAL.sub}</p>
            <ul className="mt-1 flex flex-col gap-3.5">
              {ARRIVAL.facts.map((fact) => (
                <li key={fact} className="flex items-center gap-3 text-[16px] font-semibold text-foreground">
                  <span className="grid size-[30px] shrink-0 place-items-center rounded-full bg-[var(--red-soft)] text-primary dark:text-[var(--red-dark)]">
                    <Check className="size-4" strokeWidth={2.4} />
                  </span>
                  {fact}
                </li>
              ))}
            </ul>
            <div className="mt-3 flex items-center gap-4 self-start rounded-[18px] border border-border bg-card/75 py-3.5 pr-5 pl-3.5 shadow-[0_6px_18px_rgba(26,26,23,0.07)] backdrop-blur dark:border-white/10 dark:bg-[#282828]/65 dark:shadow-[0_8px_24px_rgba(0,0,0,0.35)]">
              <Faces ring="border-card dark:border-[#2A2A2A]" />
              <span className="flex flex-col gap-1">
                <Stars />
                <span className="text-[13.5px] font-semibold text-foreground">{ARRIVAL.ratingLine}</span>
              </span>
            </div>
          </div>

          <div className="relative w-full min-w-0 min-[900px]:max-w-[520px] min-[900px]:flex-[0_0_520px]">
            <div aria-hidden="true" className="absolute -inset-x-8 -inset-y-10 rounded-[48px] bg-[radial-gradient(60%_60%_at_50%_40%,rgba(255,31,74,0.20),transparent_70%)] blur-[30px] dark:bg-[radial-gradient(60%_60%_at_50%_40%,rgba(255,61,99,0.38),transparent_70%)]" />
            <StartCard catalog={catalog} links={links} onStart={onStart} openWriting={openWriting} cardRef={cardRef} />
            <div className="absolute -top-6 -right-4 hidden items-center gap-2.5 rounded-[14px] border border-border bg-card py-2.5 pr-3.5 pl-2.5 shadow-[0_16px_36px_rgba(26,26,23,0.14)] min-[1100px]:flex dark:border-white/10 dark:bg-[#2B2B2B] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_18px_40px_rgba(0,0,0,0.55)]">
              <img src={WRITERS[1]} alt="" className="size-9 rounded-[10px] object-cover" />
              <span className="flex flex-col">
                <span className="font-display text-[16px] leading-tight font-bold text-foreground">{books}</span>
                <span className="text-[12px] text-muted-foreground">{booksLabel}</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className={cn(WRAP, "relative grid gap-4 pt-10 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))] min-[900px]:-mt-16 min-[900px]:pt-0")}>
        {ARRIVAL.stats.map(([value, label], i) => (
          <div key={label} className={cn(CARD, "flex flex-col gap-1 border-t-[3px] px-[22px] pt-[22px] pb-5", i === 0 ? "border-t-primary" : i === 3 ? "border-t-[#F59E0B] dark:border-t-[#FFB547]" : "border-t-line-2")}>
            <span className="font-display text-[32px] font-bold tracking-[-0.8px] text-foreground">{value}</span>
            <span className="text-[14px] text-muted-foreground">{label}</span>
          </div>
        ))}
      </div>

      <section className={cn(WRAP, "flex flex-col gap-9 pt-24 pb-10 max-[561px]:pt-16")}>
        <h2 className={cn(H2, "text-center")}>{ARRIVAL.stepsH}</h2>
        <ol className="relative grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
          {ARRIVAL.steps.map(([title, body], i) => (
            <li key={title} className={cn(CARD, "flex flex-col items-start gap-2.5 px-5 py-6")}>
              <span
                className={cn(
                  "grid size-[52px] place-items-center rounded-2xl font-display text-[20px] font-bold",
                  i === 0 ? "bg-primary text-primary-foreground shadow-[0_10px_24px_rgba(255,31,74,0.35)]" : "bg-[var(--red-soft)] text-primary dark:text-[var(--red-dark)]"
                )}
              >
                {i + 1}
              </span>
              <p className="mt-1 font-display text-[18px] font-semibold tracking-tight text-foreground">{title}</p>
              <p className="text-[14.5px] leading-normal text-muted-foreground">{body}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="mt-14 border-y border-border bg-secondary/60 dark:border-line-2 dark:bg-[#1A1A1A]">
        <div className={cn(WRAP, "flex flex-col gap-9 py-20 max-[561px]:py-14")}>
          <h2 className={cn(H2, "text-center")}>{ARRIVAL.checksH}</h2>
          <div className="grid grid-cols-4 gap-4 max-[700px]:grid-cols-2">
            {WRITERS.map((src) => (
              <div key={src} className="relative aspect-[4/5] overflow-hidden rounded-[20px] border border-border shadow-[0_20px_44px_rgba(26,26,23,0.16)] dark:border-line-2 dark:shadow-[0_24px_50px_rgba(0,0,0,0.55)]">
                <img src={src} alt="" className="absolute inset-0 size-full object-cover" />
                <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_55%,rgba(0,0,0,0.5)_100%)]" />
              </div>
            ))}
          </div>
          <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(260px,1fr))]">
            {ARRIVAL.checks.map(([title, body], i) => {
              const Glyph = CHECK_ICONS[i]
              return (
                <div key={title} className={cn(CARD, "flex items-start gap-4 p-6")}>
                  <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-[var(--red-soft)] text-primary dark:text-[var(--red-dark)]">
                    <Glyph className="size-[22px]" strokeWidth={1.9} />
                  </span>
                  <span className="flex flex-col gap-1.5">
                    <span className="font-display text-[17px] font-semibold text-foreground">{title}</span>
                    <span className="text-[14.5px] leading-normal text-muted-foreground">{body}</span>
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      <section className={cn(WRAP, "flex flex-col gap-9 py-24 max-[561px]:py-16")}>
        <div className="flex flex-col items-center gap-2.5 text-center">
          <h2 className={H2}>{ARRIVAL.reviewsH}</h2>
          <p className="flex items-center gap-2 text-[15px] font-semibold text-muted-foreground">
            <Stars />
            {ARRIVAL.ratingLine}
          </p>
        </div>
        <div className="flex flex-wrap items-stretch gap-4">
          <Review text={featured} featured />
          <div className="flex min-w-0 flex-[1_1_340px] flex-col gap-4">
            {others.map((text) => <Review key={text} text={text} />)}
          </div>
        </div>
      </section>

      <section className={cn(WRAP, "pb-[88px]")}>
        <div className="relative flex flex-wrap items-center justify-between gap-10 overflow-hidden rounded-[28px] p-16 [background:linear-gradient(135deg,#2A1A1E_0%,#1A1A17_60%)] shadow-[0_30px_70px_rgba(26,26,23,0.25)] max-[561px]:p-8 dark:[background:linear-gradient(135deg,#2A1A1E_0%,#1C1C1C_60%)] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_30px_70px_rgba(0,0,0,0.5)]">
          <div aria-hidden="true" className="absolute -top-40 -right-32 size-[520px] rounded-full bg-[radial-gradient(circle,rgba(255,61,99,0.55),transparent_65%)]" />
          <div className="relative flex max-w-[600px] flex-col gap-3">
            <p className="font-display text-[36px] leading-[1.12] font-bold tracking-[-0.9px] text-[#F2F2F2] max-[561px]:text-[26px]">{ARRIVAL.headline}</p>
            <p className="text-[15px] leading-normal text-[#F2F2F2]/70">{GUEST.paymentTiming}</p>
          </div>
          <div className="relative flex flex-wrap items-center gap-6">
            <Faces size="size-[52px]" ring="border-[#2A1A1E]" />
            <button
              type="button"
              onClick={toStart}
              className="group inline-flex cursor-pointer items-center gap-2.5 rounded-[14px] bg-primary px-7 py-4 text-[17px] font-bold text-primary-foreground shadow-[0_14px_34px_rgba(255,31,74,0.45)] outline-none transition-[background-color,box-shadow,transform] duration-150 hover:-translate-y-0.5 hover:bg-[#E0123B] hover:shadow-[0_20px_44px_rgba(255,31,74,0.6)] focus-visible:ring-4 focus-visible:ring-white/40 active:translate-y-0"
            >
              {GUEST.startH}
              <ArrowRight className="size-[18px] transition-transform duration-150 group-hover:translate-x-1" strokeWidth={2.3} />
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}
